import Swal from "sweetalert2";
import api from "../services/api";
import { showToast } from "../helper/notifications";

function copyTextToClipboard(text) {
  try {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.setAttribute("readonly", "");
    textarea.style.position = "fixed";
    textarea.style.top = "-9999px";
    textarea.style.left = "-9999px";
    document.body.appendChild(textarea);
    textarea.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(textarea);
    if (ok) return Promise.resolve(true);
  } catch (e) {
    // ignore fallback error
  }
  if (navigator.clipboard && navigator.clipboard.writeText) {
    return navigator.clipboard
      .writeText(text)
      .then(() => true)
      .catch(() => false);
  }
  return Promise.resolve(false);
}

function openUrlOrPrompt(url, sheetTitle) {
  const win = window.open(url, "_blank", "noopener,noreferrer");
  if (!win) {
    Swal.fire({
      title: "Google Sheet Ready",
      html: `<p class="text-sm text-secondary-light mb-0">Your spreadsheet <b>${sheetTitle || "Export"}</b> has been created.</p>`,
      icon: "success",
      confirmButtonText: "Open Google Sheet",
      confirmButtonColor: "#0f9d58",
    }).then((r) => {
      if (r.isConfirmed) {
        window.open(url, "_blank", "noopener,noreferrer");
      }
    });
  }
}

/**
 * Exports tabular data (headers + rows) to Google Sheets.
 * 1. Copies TSV (Tab-Separated Values) to the clipboard immediately.
 * 2. Attempts direct Google Sheets creation via the backend Google Sheets API.
 * 3. If OAuth scope for Google Sheets is not yet granted, offers 1-click popup authorization
 *    or instant opening of https://sheets.new with clipboard data ready to paste (Cmd+V / Ctrl+V).
 */
export async function exportToGoogleSheets({ title, headers = [], rows = [] }) {
  if (!rows || rows.length === 0) {
    showToast("info", "No data to export.");
    return;
  }

  // 1. Build TSV (Tab-Separated Values) for Google Sheets clipboard compatibility
  const sanitizeCell = (val) =>
    String(val ?? "")
      .replace(/\t/g, " ")
      .replace(/\r?\n/g, " ");

  const tsvLines = [
    headers.map(sanitizeCell).join("\t"),
    ...rows.map((row) => (Array.isArray(row) ? row.map(sanitizeCell).join("\t") : "")),
  ];
  const tsvContent = tsvLines.join("\n");

  const copiedToClipboard = await copyTextToClipboard(tsvContent);

  // 2. Try direct creation via Backend Google Sheets API
  try {
    const res = await api.post("/google-business/export-sheets", {
      title,
      headers,
      rows,
    });

    if (res.data?.success && res.data?.spreadsheetUrl) {
      showToast("success", "Google Sheet created successfully!");
      openUrlOrPrompt(res.data.spreadsheetUrl, title);
      return;
    }
  } catch (apiErr) {
    console.warn("Direct Google Sheets API export fallback:", apiErr?.response?.data || apiErr.message);
  }

  // 3. Fallback: Interactive modal to open sheets.new (with TSV in clipboard) or authorize via popup
  const result = await Swal.fire({
    title: "Export to Google Sheets",
    html: `
      <div class="text-start text-sm">
        <div class="p-12 radius-8 bg-success-focus border border-success-main mb-12 d-flex align-items-start gap-2">
          <span class="text-success-main fw-bold">✓</span>
          <div>
            <strong class="text-neutral-900">${rows.length} rows (${headers.length} columns) copied to clipboard!</strong>
            <div class="text-xs text-secondary-light mt-1">
              Click <b>Open New Google Sheet</b> below and press <kbd>Cmd + V</kbd> (or <kbd>Ctrl + V</kbd>) in cell <b>A1</b> to insert the entire table immediately.
            </div>
          </div>
        </div>
        <div class="text-xs text-secondary-light">
          Or click <b>Authorize & Auto-Create</b> once to grant Google Sheets API access and create spreadsheets directly with 1 click.
        </div>
      </div>
    `,
    icon: copiedToClipboard ? "success" : "info",
    showCancelButton: true,
    showDenyButton: true,
    confirmButtonText: "Open New Google Sheet",
    denyButtonText: "Authorize & Auto-Create",
    cancelButtonText: "Close",
    confirmButtonColor: "#0f9d58",
    denyButtonColor: "#2563eb",
    preConfirm: () => {
      copyTextToClipboard(tsvContent);
      window.open("https://sheets.new", "_blank", "noopener,noreferrer");
      return true;
    },
  });

  if (result.isDenied) {
    try {
      const authRes = await api.get("/google-business/connect-url?popup=1");
      if (!authRes.data?.url) {
        showToast("error", "Could not generate Google authorization URL.");
        return;
      }

      const popup = window.open(
        authRes.data.url,
        "google_sheets_oauth",
        "width=560,height=680,menubar=no,toolbar=no,location=no,status=no"
      );

      if (!popup) {
        window.location.href = authRes.data.url;
        return;
      }

      // Listen for popup completion and automatically retry creating the Google Sheet
      const handleMessage = async (event) => {
        if (event.data?.type === "GOOGLE_OAUTH_SUCCESS") {
          window.removeEventListener("message", handleMessage);
          try {
            showToast("info", "Creating Google Sheet...");
            const retryRes = await api.post("/google-business/export-sheets", {
              title,
              headers,
              rows,
            });
            if (retryRes.data?.success && retryRes.data?.spreadsheetUrl) {
              showToast("success", "Google Sheet created successfully!");
              openUrlOrPrompt(retryRes.data.spreadsheetUrl, title);
            } else {
              showToast(
                "warning",
                retryRes.data?.error ||
                  "Google Sheets API could not create the file. Opening blank sheet with clipboard data."
              );
              copyTextToClipboard(tsvContent);
              window.open("https://sheets.new", "_blank", "noopener,noreferrer");
            }
          } catch (retryErr) {
            showToast("error", "Failed to create Google Sheet after authorization.");
          }
        }
      };

      window.addEventListener("message", handleMessage);
    } catch (err) {
      showToast("error", "Could not start Google authorization.");
    }
  }
}
